// src/components/PortfolioSimulatorCsv.js
import React, { useState, useEffect, useMemo, useRef, useContext } from "react";
import Papa from "papaparse";
import { Line } from "react-chartjs-2";
import { Chart, registerables } from "chart.js";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "./PortfolioSimulatorCsv.css";
import { MARKETING_LABEL } from "../config/compliance";
import { LangContext } from "../lang/LangProvider";
import { useAnalytics } from "../hooks/useAnalytics";

Chart.register(...registerables);

// ===================== i18n helpers ===================== //
function L(lang, it, en) { return lang === "en" ? en : it; }

// Richiesta: tutto in dollari, nessuna conversione
const CURRENCY = "$";

// ===================== Utilities ===================== //
const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

function getMonthlyEOM(series) {
  const sorted = series
    .map(({ date, price }) => ({ d: new Date(date), price: Number(price) }))
    .filter((x) => Number.isFinite(x.price))
    .sort((a, b) => a.d - b.d);
  const lastByMonth = new Map();
  for (const row of sorted) {
    const k = monthKey(row.d);
    const prev = lastByMonth.get(k);
    if (!prev || row.d > prev.d) lastByMonth.set(k, row);
  }
  const labels = Array.from(lastByMonth.keys()).sort();
  const prices = labels.map((k) => lastByMonth.get(k).price);
  return { labels, prices };
}

function calcLogReturns(prices) {
  const out = [];
  for (let i = 1; i < prices.length; i++) {
    const prev = prices[i - 1], curr = prices[i];
    out.push(prev > 0 && curr > 0 ? Math.log(curr / prev) : 0);
  }
  return out;
}

function sharpeSortinoFromLog(lr, periodsPerYear = 12) {
  if (!lr.length) return { sharpe: 0, sortino: 0 };
  const mean = lr.reduce((s, r) => s + r, 0) / lr.length;
  const std = Math.sqrt(lr.reduce((s, r) => s + (r - mean) * (r - mean), 0) / lr.length) || 0;
  const downs = lr.filter((r) => r < 0);
  const sdDown = downs.length ? Math.sqrt(downs.reduce((s, r) => s + r * r, 0) / downs.length) : 0;
  return {
    sharpe: std ? (mean / std) * Math.sqrt(periodsPerYear) : 0,
    sortino: sdDown ? (mean / sdDown) * Math.sqrt(periodsPerYear) : 0,
  };
}

function indexFromLog(lr) {
  const idx = [1];
  for (let i = 0; i < lr.length; i++) idx.push(idx[idx.length - 1] * Math.exp(lr[i]));
  return idx;
}

function maxDrawdownFromIndex(idx) {
  let peak = idx[0];
  let mdd = 0;
  for (const v of idx) {
    peak = Math.max(peak, v);
    const dd = (peak - v) / peak;
    if (dd > mdd) mdd = dd;
  }
  return mdd;
}

function ulcerIndexFromIndex(idx) {
  if (!idx.length) return 0;

  // trova il primo valore positivo (evita problemi con 0 o valori non validi)
  const start = idx.findIndex((v) => v > 0 && Number.isFinite(v));
  if (start === -1) return 0; // tutti zero o non validi

  let peak = idx[start];
  const pctDD = [];

  for (let i = start; i < idx.length; i++) {
    const v = idx[i];
    peak = Math.max(peak, v);
    const ddPct = ((v - peak) / peak) * 100; // <= 0
    if (Number.isFinite(ddPct)) pctDD.push(ddPct);
  }

  if (!pctDD.length) return 0;

  const meanSq =
    pctDD.reduce((s, x) => s + x * x, 0) / pctDD.length;

  return Math.sqrt(meanSq);
}


function timeUnderWater(idx) {
  let peak = idx[0];
  const inDD = [];
  for (const v of idx) {
    peak = Math.max(peak, v);
    inDD.push(v < peak);
  }
  const durations = [];
  let cur = 0;
  for (const f of inDD) {
    if (f) cur += 1;
    else if (cur > 0) {
      durations.push(cur);
      cur = 0;
    }
  }
  if (cur > 0) durations.push(cur);
  const avg = durations.length ? durations.reduce((s, x) => s + x, 0) / durations.length : 0;
  const mx = durations.length ? Math.max(...durations) : 0;
  return { avg, max: mx };
}

function percentile(arr, p) {
  if (!arr.length) return 0;
  const sorted = arr.slice().sort((a, b) => a - b);
  const idx = Math.floor((p / 100) * sorted.length);
  return sorted[idx];
}

function validateWeights(weights, keys) {
  const sum = keys.reduce((s, k) => s + (weights[k] || 0), 0);
  return Math.abs(sum - 1) < 1e-8;
}

// ===================== Value formatting (fix IT/EN) ===================== //
function formatMetricValue(label, value) {
  if (typeof value !== "number" || !isFinite(value)) return String(value);

  const lower = label.toLowerCase();

  // currency-like labels (IT + EN)
  const isCurrency =
    /\b(capitale|capital)\b/.test(lower);

  // integer-like labels (counts, periods, durations; IT + EN)
  const isCount =
    /(under water|mesi|gg|periods|days|months)/.test(lower);

  if (isCurrency) {
    // niente conversione, stampa in $
    return `${value.toFixed(0)}${CURRENCY}`;
  }
  if (isCount) {
    return `${Math.round(value)}`;
  }
  // default: percentuale (fractions → %)
  return `${(value * 100).toFixed(2)}%`;
}

// ===================== Charts ===================== //
const HibbsChart = React.forwardRef(({ simResults, lang }, ref) => {
  if (!simResults?.length) return null;
  const n = simResults[0].length;
  const percentiles = [5, 50, 95].map((p) =>
    Array.from({ length: n }, (_, i) => {
      const col = simResults.map((sim) => sim[i]).sort((a, b) => a - b);
      return col[Math.floor((simResults.length * p) / 100)];
    })
  );
  const labels = Array.from({ length: n }, (_, i) => (i / 12).toFixed(1));
  return (
    <Line
      ref={ref}
      data={{
        labels,
        datasets: [
          { label: L(lang, "5° percentile", "5th percentile"), data: percentiles[0], borderColor: "rgba(233,30,99,1)", fill: false },
          { label: L(lang, "Mediana", "Median"), data: percentiles[1], borderColor: "rgba(25,118,210,1)", fill: false },
          {
            label: L(lang, "95° percentile", "95th percentile"),
            data: percentiles[2],
            borderColor: "rgba(76,175,80,1)",
            fill: "+1",
            backgroundColor: "rgba(76,175,80,0.10)",
          },
        ],
      }}
      options={{
        responsive: true,
        plugins: { legend: { position: "bottom" } },
        scales: {
          x: { title: { display: true, text: L(lang, "Anni", "Years") } },
          y: { title: { display: true, text: L(lang, `Capitale (${CURRENCY})`, `Capital (${CURRENCY})`) } },
        },
      }}
    />
  );
});

const BacktestChart = React.forwardRef(({ history, labels, lang }, ref) => {
  if (!history?.length) return null;
  return (
    <Line
      ref={ref}
      data={{
        labels,
        datasets: [
          {
            label: L(lang, "Capitale (con versamenti)", "Capital (with deposits)"),
            data: history,
            borderColor: "rgba(25,118,210,1)",
            fill: false,
          },
        ],
      }}
      options={{
        responsive: true,
        plugins: { legend: { position: "bottom" } },
        scales: {
          x: { title: { display: true, text: L(lang, "Data", "Date") } },
          y: { title: { display: true, text: L(lang, `Capitale (${CURRENCY})`, `Capital (${CURRENCY})`) } },
        },
      }}
    />
  );
});

function MetricsTable({ metrics, title }) {
  if (!metrics) return null;
  return (
    <div style={{ marginTop: "1rem" }}>
      <h4>{title}</h4>
      <table className="sim-metrics">
        <tbody>
          {Object.entries(metrics).map(([k, v]) => (
            <tr key={k}>
              <td>{k}:</td>
              <td>{formatMetricValue(k, v)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ===================== Main Component ===================== //
export default function PortfolioSimulatorCsv() {
  const { lang } = useContext(LangContext);
  const { trackToolStart, trackSimulationRun } = useAnalytics();

  // Traccia apertura tool
  useEffect(() => {
    trackToolStart("portfolio_simulator", lang);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // CSV sorgente (nessuna conversione valutaria)
  const CSV_URL = "/portfolio_etf_demo.csv";

  const [data, setData] = useState([]);
  const [etfNames, setEtfNames] = useState([]);
  const [selectedEtfs, setSelectedEtfs] = useState([]);
  const [weights, setWeights] = useState({});

  const [years, setYears] = useState(10);
  const [initial, setInitial] = useState(10000);
  const [monthly, setMonthly] = useState(250);
  const [simulations, setSimulations] = useState(400);

  const [simResults, setSimResults] = useState(null);

  // Backtest: separo Portafoglio vs Investitore
  const [backtestPortfolioMetrics, setBacktestPortfolioMetrics] = useState(null);
  const [backtestInvestorMetrics, setBacktestInvestorMetrics] = useState(null);

  // Monte Carlo: separo Portafoglio vs Investitore
  const [simPortfolioMetrics, setSimPortfolioMetrics] = useState(null);
  const [simInvestorMetrics, setSimInvestorMetrics] = useState(null);
  const [error, setError] = useState("");
  // reset delle metriche di simulazione quando cambia lingua
  useEffect(() => {
    setSimPortfolioMetrics(null);
    setSimInvestorMetrics(null);
    // opzionale: se vuoi nascondere anche il grafico Hibbs dopo il cambio lingua:
    // setSimResults(null);
  }, [lang]);

  const backtestRef = useRef();
  const simRef = useRef();

  // --------------- Load CSV once --------------- //
  useEffect(() => {
    fetch(CSV_URL)
      .then((res) => res.text())
      .then((text) => {
        Papa.parse(text, {
          header: true,
          skipEmptyLines: true,
          complete: ({ data: raw }) => {
            setData(raw);
            const keys = Object.keys(raw[0]).filter((k) => !/date/i.test(k));
            setEtfNames(keys);
            const u = 1 / keys.length;
            const w = {};
            keys.forEach((k) => (w[k] = u));
            setWeights(w);
            setSelectedEtfs(keys);
          },
        });
      });
  }, [CSV_URL]);

  // --------------- Monthly maps (EoM) --------------- //
  const monthlyMaps = useMemo(() => {
    const m = {};
    etfNames.forEach((etf) => {
      const series = data.map((r) => ({ date: r.date, price: parseFloat(r[etf]) }));
      const { labels, prices } = getMonthlyEOM(series);
      const map = new Map();
      labels.forEach((lab, i) => map.set(lab, prices[i]));
      m[etf] = map;
    });
    return m;
  }, [data, etfNames]);

  // --------------- Aligned monthly dataset --------------- //
  const { alignedLabels, pricesByEtf } = useMemo(() => {
    if (!selectedEtfs.length) return { alignedLabels: [], pricesByEtf: {} };
    let common = null;
    for (const etf of selectedEtfs) {
      const keys = Array.from((monthlyMaps[etf] || new Map()).keys());
      common = common ? common.filter((k) => monthlyMaps[etf].has(k)) : keys;
    }
    if (!common) return { alignedLabels: [], pricesByEtf: {} };
    common.sort();
    const pricesByEtf = {};
    for (const etf of selectedEtfs) {
      pricesByEtf[etf] = common.map((k) => monthlyMaps[etf].get(k));
    }
    return { alignedLabels: common, pricesByEtf };
  }, [selectedEtfs, monthlyMaps]);

  // --------------- Portfolio returns / index / wealth --------------- //
  const { portfolioLogRets, indexPath, wealthPath } = useMemo(() => {
    if (!selectedEtfs.length || !alignedLabels.length) return { portfolioLogRets: [], indexPath: [], wealthPath: [] };

    const logRetsByEtf = {};
    selectedEtfs.forEach((etf) => {
      logRetsByEtf[etf] = calcLogReturns(pricesByEtf[etf]);
    });

    const T = logRetsByEtf[selectedEtfs[0]].length;
    const portLog = new Array(T).fill(0);
    for (let t = 0; t < T; t++) {
      let r = 0;
      for (const etf of selectedEtfs) r += (weights[etf] || 0) * logRetsByEtf[etf][t];
      portLog[t] = r; // rebalancing mensile implicito
    }

    const idx = indexFromLog(portLog);

    const wealth = [initial];
    for (let t = 0; t < T; t++) {
      const afterDeposit = wealth[wealth.length - 1] + monthly;
      wealth.push(afterDeposit * Math.exp(portLog[t]));
    }

    return { portfolioLogRets: portLog, indexPath: idx, wealthPath: wealth };
  }, [selectedEtfs, alignedLabels, pricesByEtf, weights, initial, monthly]);

  // --------------- Backtest metrics (Monthly) --------------- //
  useEffect(() => {
    if (!indexPath.length || !wealthPath.length) {
      setBacktestPortfolioMetrics(null);
      setBacktestInvestorMetrics(null);
      return;
    }

    // --- Portafoglio (indice, senza flussi) ---
    const yrs = (indexPath.length - 1) / 12;
    const cagrIndex =
      yrs > 0
        ? Math.pow(indexPath[indexPath.length - 1] / indexPath[0], 1 / yrs) - 1
        : 0;

    const { sharpe: sharpeIndex, sortino: sortinoIndex } =
      sharpeSortinoFromLog(portfolioLogRets, 12);

    const mddIndex = maxDrawdownFromIndex(indexPath);
    const uiIndex = ulcerIndexFromIndex(indexPath);
    const tuwIndex = timeUnderWater(indexPath);

    setBacktestPortfolioMetrics({
      [L(lang, "CAGR storico (indice)", "Historical CAGR (index)")]: cagrIndex,
      [L(lang, "Sharpe storico (indice)", "Historical Sharpe (index)")]:
        sharpeIndex,
      [L(lang, "Sortino storico (indice)", "Historical Sortino (index)")]:
        sortinoIndex,
      [L(
        lang,
        "Max Drawdown storico (indice)",
        "Historical Max Drawdown (index)"
      )]: mddIndex,
      "Ulcer Index (index)": uiIndex / 100,
      [L(
        lang,
        "Avg Time Under Water (mesi, indice)",
        "Avg Time Under Water (months, index)"
      )]: tuwIndex.avg,
      [L(
        lang,
        "Max Time Under Water (mesi, indice)",
        "Max Time Under Water (months, index)"
      )]: tuwIndex.max,
    });

    // --- Investitore (ricchezza, con versamenti) ---
    const invested = initial + monthly * (wealthPath.length - 1);
    const finalCap = wealthPath[wealthPath.length - 1] || initial;

    const mddWealth = maxDrawdownFromIndex(wealthPath);
    const uiWealth = ulcerIndexFromIndex(wealthPath);
    const tuwWealth = timeUnderWater(wealthPath);

    setBacktestInvestorMetrics({
      [L(lang, "Capitale investito", "Invested capital")]: invested,
      [L(lang, "Capitale finale", "Final capital")]: finalCap,
      [L(
        lang,
        "Max Drawdown (ricchezza)",
        "Max Drawdown (wealth)"
      )]: mddWealth,
      "Ulcer Index (wealth)": uiWealth / 100,
      [L(
        lang,
        "Avg Time Under Water (mesi, ricchezza)",
        "Avg Time Under Water (months, wealth)"
      )]: tuwWealth.avg,
      [L(
        lang,
        "Max Time Under Water (mesi, ricchezza)",
        "Max Time Under Water (months, wealth)"
      )]: tuwWealth.max,
    });
  }, [indexPath, portfolioLogRets, wealthPath, initial, monthly, lang]);



  // --------------- Monte Carlo (mensile) --------------- //
  const { corrMatrix, means, stdevs } = useMemo(() => {
    if (!selectedEtfs.length || !alignedLabels.length) return { corrMatrix: [], means: [], stdevs: [] };

    const R = selectedEtfs.map((etf) => calcLogReturns(pricesByEtf[etf]));
    const T = R[0].length;
    const means = R.map((row) => row.reduce((s, v) => s + v, 0) / T);
    const stdevs = R.map((row, i) => {
      const m = means[i];
      const v = row.reduce((s, x) => s + (x - m) * (x - m), 0) / T;
      return Math.sqrt(Math.max(v, 0));
    });
    const cov = Array.from({ length: R.length }, () => Array(R.length).fill(0));
    for (let i = 0; i < R.length; i++) {
      for (let j = 0; j < R.length; j++) {
        let c = 0;
        for (let t = 0; t < T; t++) c += (R[i][t] - means[i]) * (R[j][t] - means[j]);
        cov[i][j] = c / (T - 1);
      }
    }
    const corr = cov.map((row, i) =>
      row.map((v, j) => {
        const denom = Math.sqrt(cov[i][i] * cov[j][j]);
        return denom > 0 ? v / denom : i === j ? 1 : 0;
      })
    );
    return { corrMatrix: corr, means, stdevs };
  }, [selectedEtfs, alignedLabels, pricesByEtf]);

  function cholesky(matrix) {
    const n = matrix.length;
    const L = Array.from({ length: n }, () => Array(n).fill(0));
    for (let i = 0; i < n; i++) {
      for (let j = 0; j <= i; j++) {
        let sum = matrix[i][j];
        for (let k = 0; k < j; k++) sum -= L[i][k] * L[j][k];
        L[i][j] = i === j ? Math.sqrt(Math.max(sum, 0)) : sum / L[j][j];
      }
    }
    return L;
  }

  function sampleMultivariate(mean, stdev, corr) {
    const n = mean.length;
    const z = Array.from({ length: n }, () => {
      const u1 = Math.random(), u2 = Math.random();
      return Math.sqrt(-2 * Math.log(Math.max(u1, 1e-12))) * Math.cos(2 * Math.PI * u2);
    });
    const L = cholesky(corr);
    const correlated = Array(n).fill(0);
    for (let i = 0; i < n; i++) for (let k = 0; k < n; k++) correlated[i] += L[i][k] * z[k];
    return mean.map((mu, i) => mu + stdev[i] * correlated[i]);
  }

  const validWeights = validateWeights(weights, selectedEtfs);

  function validateInputs() {
    if (!validWeights) return L(lang, "La somma dei pesi deve essere 1", "Weights must sum to 1");
    if (years < 1) return L(lang, "Orizzonte non valido", "Invalid horizon");
    if (initial < 0) return L(lang, "Capitale iniziale non valido", "Invalid initial capital");
    if (monthly < 0) return L(lang, "Versamento mensile non valido", "Invalid monthly deposit");
    return "";
  }

  const handleSimula = () => {
    const err = validateInputs();
    if (err) {
      setError(err);
      return;
    }
    setError("");

    const nSteps = years * 12;

    const indexPaths = [];
    const wealthPaths = [];

    for (let s = 0; s < simulations; s++) {
      let idx = 1;       // indice base 1 (senza flussi)
      let cap = initial; // capitale con versamenti

      const idxPath = [idx];
      const wealthPathSim = [cap];

      for (let t = 0; t < nSteps; t++) {
        // versamento a inizio mese
        cap += monthly;

        // log-returns simulati degli ETF
        const rv = sampleMultivariate(means, stdevs, corrMatrix);

        let r = 0;
        for (let i = 0; i < selectedEtfs.length; i++) {
          const etf = selectedEtfs[i];
          r += (weights[etf] || 0) * rv[i];
        }

        // aggiorno indice e ricchezza
        idx = idx * Math.exp(r);
        cap = cap * Math.exp(r);

        idxPath.push(idx);
        wealthPathSim.push(cap);
      }

      indexPaths.push(idxPath);
      wealthPaths.push(wealthPathSim);
    }

    // per il grafico Hibbs continuiamo a usare le path di ricchezza
    setSimResults(wealthPaths);
    trackSimulationRun(selectedEtfs.length, years);

    // --- Metriche Portafoglio (indice, no flussi) ---
    const finalIndex = indexPaths
      .map((p) => p[p.length - 1])
      .sort((a, b) => a - b);

    const medIndex = percentile(finalIndex, 50);
    const cagrIndexSim = Math.pow(medIndex, 1 / years) - 1; // parte da 1

    const midIdx = Math.floor(indexPaths.length / 2);
    const idxVals = indexPaths[midIdx];

    let sum = 0,
      sum2 = 0;
    const downs = [];

    for (let i = 1; i < idxVals.length; i++) {
      const rr = Math.log(idxVals[i] / idxVals[i - 1]); // puro rendimento
      sum += rr;
      sum2 += rr * rr;
      if (rr < 0) downs.push(rr);
    }

    const avg = sum / (idxVals.length - 1);
    const std =
      Math.sqrt(
        Math.max(sum2 / (idxVals.length - 1) - avg * avg, 0)
      ) || 0;
    const sdDown = downs.length
      ? Math.sqrt(
          downs.reduce((s, r) => s + r * r, 0) / downs.length
        )
      : 0;

    const sharpeSimIndex = std ? (avg / std) * Math.sqrt(12) : 0;
    const sortinoSimIndex = sdDown ? (avg / sdDown) * Math.sqrt(12) : 0;
    const mddSimIndex = maxDrawdownFromIndex(idxVals);
    const uiSimIndex = ulcerIndexFromIndex(idxVals);

    setSimPortfolioMetrics({
      [L(
        lang,
        "CAGR simulazione (indice)",
        "Simulation CAGR (index)"
      )]: cagrIndexSim,
      [L(
        lang,
        "Sharpe simulazione (indice)",
        "Simulation Sharpe (index)"
      )]: sharpeSimIndex,
      [L(
        lang,
        "Sortino simulazione (indice)",
        "Simulation Sortino (index)"
      )]: sortinoSimIndex,
      [L(
        lang,
        "Max Drawdown simulazione (indice)",
        "Simulation Max Drawdown (index)"
      )]: mddSimIndex,
      "Ulcer Index (index)": uiSimIndex / 100,
    });

    // --- Metriche Investitore (ricchezza, con versamenti) ---
    const investSim = initial + monthly * nSteps;

    const lastWealth = wealthPaths
      .map((p) => p[p.length - 1])
      .sort((a, b) => a - b);

    const p5 = percentile(lastWealth, 5);
    const medWealth = percentile(lastWealth, 50);
    const p95 = percentile(lastWealth, 95);

    const cagrInvestorSim =
      medWealth > 0 && investSim > 0
        ? Math.pow(medWealth / investSim, 1 / years) - 1
        : 0;

    const midWealth = wealthPaths[Math.floor(wealthPaths.length / 2)];
    const mddWealthSim = maxDrawdownFromIndex(midWealth);

    setSimInvestorMetrics({
      [L(lang, "Capitale investito", "Invested capital")]: investSim,
      [L(
        lang,
        "Capitale finale (mediano)",
        "Final capital (median)"
      )]: medWealth,
      [L(
        lang,
        "CAGR simulazione (investitore)",
        "Simulation CAGR (investor)"
      )]: cagrInvestorSim,
      [L(
        lang,
        "Max Drawdown (ricchezza simulata)",
        "Max Drawdown (simulated wealth)"
      )]: mddWealthSim,
      [L(lang, "Capitale 5%", "5% capital")]: p5,
      [L(lang, "Capitale 95%", "95% capital")]: p95,
    });
  };


  // --------------- PDF export --------------- //
  const handleDownloadPdf = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(16);
    doc.text(L(lang, "Report Backtest e Simulazione", "Backtest & Simulation Report"), 14, 16);
    doc.setFontSize(10);
    doc.text(`${L(lang, "Data", "Date")}: ${new Date().toLocaleString()}`, 14, 24);

    const backChart = backtestRef.current;
    if (backChart?.toBase64Image) doc.addImage(backChart.toBase64Image(), "PNG", 14, 30, 120, 60);
    const simChart = simRef.current;
    if (simChart?.toBase64Image) doc.addImage(simChart.toBase64Image(), "PNG", 140, 30, 120, 60);

    // input
    doc.setFontSize(12);
    doc.text(L(lang, "Parametri di Input", "Input Parameters"), 14, 100);
    doc.setFontSize(10);
    let y = 108;
    doc.text(`${L(lang, "Iniziale", "Initial")}: ${CURRENCY}${initial}`, 14, y); y += 6;
    doc.text(`${L(lang, "Mensile", "Monthly")}: ${CURRENCY}${monthly}`, 14, y); y += 6;
    doc.text(`${L(lang, "Orizzonte", "Horizon")}: ${years} ${L(lang, "anni", "years")}`, 14, y); y += 6;
    doc.text(`${L(lang, "Simulazioni", "Simulations")}: ${simulations}`, 14, y); y += 10;
    doc.text(L(lang, "Pesi ETF:", "ETF Weights:"), 14, y); y += 6;
    selectedEtfs.forEach((etf) => { doc.text(`${etf}: ${(weights[etf] * 100).toFixed(1)}%`, 18, y); y += 6; });

    // Backtest tables
    if (backtestPortfolioMetrics) {
      autoTable(doc, {
        startY: y + 6,
        head: [[
          L(lang, "Metriche Backtest – Portafoglio (no flussi)", "Backtest Metrics – Portfolio (no flows)"),
          L(lang, "Valore", "Value"),
        ]],
        body: Object.entries(backtestPortfolioMetrics).map(([k, v]) => [
          k,
          formatMetricValue(k, v),
        ]),
        theme: "grid",
        headStyles: { fillColor: "#f1f3f6", textColor: "#15244a" },
      });
      y = doc.lastAutoTable.finalY + 8;
    }

    if (backtestInvestorMetrics) {
      autoTable(doc, {
        startY: y,
        head: [[
          L(lang, "Metriche Backtest – Investitore (con versamenti)", "Backtest Metrics – Investor (with contributions)"),
          L(lang, "Valore", "Value"),
        ]],
        body: Object.entries(backtestInvestorMetrics).map(([k, v]) => [
          k,
          formatMetricValue(k, v),
        ]),
        theme: "grid",
        headStyles: { fillColor: "#f1f3f6", textColor: "#15244a" },
      });
      y = doc.lastAutoTable.finalY + 8;
    }

    // Simulation tables
    if (simPortfolioMetrics) {
      autoTable(doc, {
        startY: y,
        head: [[
          L(lang, "Metriche Simulazione – Portafoglio (no flussi)", "Simulation Metrics – Portfolio (no flows)"),
          L(lang, "Valore", "Value"),
        ]],
        body: Object.entries(simPortfolioMetrics).map(([k, v]) => [
          k,
          formatMetricValue(k, v),
        ]),
        theme: "grid",
        headStyles: { fillColor: "#f1f3f6", textColor: "#15244a" },
      });
      y = doc.lastAutoTable.finalY + 8;
    }

    if (simInvestorMetrics) {
      autoTable(doc, {
        startY: y,
        head: [[
          L(lang, "Metriche Simulazione – Investitore (con versamenti)", "Simulation Metrics – Investor (with contributions)"),
          L(lang, "Valore", "Value"),
        ]],
        body: Object.entries(simInvestorMetrics).map(([k, v]) => [
          k,
          formatMetricValue(k, v),
        ]),
        theme: "grid",
        headStyles: { fillColor: "#f1f3f6", textColor: "#15244a" },
      });
      y = doc.lastAutoTable.finalY + 8;
    }


    // disclaimer
    doc.setFontSize(9);
    const txt = lang === "en"
      ? [
          "Disclaimer and limitation of liability",
          "",
          "This report is generated automatically for informational purposes only.",
          "Nothing in this document constitutes personalized financial advice, nor an offer or solicitation to buy, sell or hold financial instruments.",
          "The simulations and backtests presented here are based on historical data and quantitative models: past returns do not guarantee future performance.",
          "No warranty is made as to the accuracy, completeness or timeliness of the information.",
          "Before making any investment decision, you should consult a licensed financial advisor.",
          "",
        ].join("\n")
      : [
          "Disclaimer e limitazione di responsabilità",
          "",
          "Il presente report è generato automaticamente a scopo esclusivamente informativo.",
          "Nessuna parte di questo documento costituisce consulenza finanziaria personalizzata, né offerta o sollecitazione all’acquisto, vendita o detenzione di strumenti finanziari.",
          "Le simulazioni e i backtest qui riportati sono basati su dati storici e modelli quantitativi: i rendimenti passati non garantiscono prestazioni analoghe in futuro.",
          "Nessuna garanzia è prestata in merito all’accuratezza, completezza o tempestività delle informazioni.",
          "Prima di prendere qualsiasi decisione di investimento, è necessario rivolgersi a un consulente finanziario abilitato.",
          "",
        ].join("\n");
    doc.text(txt, 14, y, { maxWidth: 260 });
    doc.save(L(lang, "report_backtest_simulazione.pdf", "backtest_simulation_report.pdf"));
  };

  // --------------- UI --------------- //
  const valid = validateWeights(weights, selectedEtfs);

  return (
    <div className="simulator" aria-label={MARKETING_LABEL}>
      <h2>{L(lang, "Simulatore Portafoglio Quantitativo", "Quantitative Portfolio Simulator")}</h2>
      <div className="sim-premium-intro">
        <p>
          {L(
            lang,
            "Backtest (storico EoM allineato) e Monte Carlo. Il drawdown è calcolato su un indice di performance senza flussi; il grafico mostra la ricchezza con versamenti. Correlazioni e volatilità sono stimate sui rendimenti logaritmici mensili allineati (EoM), con ribilanciamento mensile implicito.",
            "Backtest (aligned EoM history) and Monte Carlo. Drawdown is computed on a performance index without flows; the chart shows wealth with deposits. Correlations and volatility are estimated on aligned monthly log returns (EoM), with implicit monthly rebalancing."
          )}
        </p>
      </div>

      <div className="sim-disclaimer">
        <b>DISCLAIMER:</b>{" "}
        {L(
          lang,
          "I backtest e le simulazioni sono a scopo illustrativo; non sono consulenza finanziaria né garanzia di risultati; l’accuratezza dei dati non è garantita; le decisioni d’investimento sono a esclusivo rischio dell’utente.",
          "Backtests and simulations are for illustrative purposes only; they do not constitute financial advice nor guarantee results; data accuracy is not guaranteed; investment decisions are at the user’s sole risk."
        )}
      </div>

      <h3>{L(lang, "Step 1: Seleziona ETF", "Step 1: Select ETFs")}</h3>
      <div className="etf-selector">
        {etfNames.map((etf) => (
          <label key={etf}>
            <input
              type="checkbox"
              checked={selectedEtfs.includes(etf)}
              onChange={() => {
                const next = selectedEtfs.includes(etf)
                  ? selectedEtfs.filter((x) => x !== etf)
                  : [...selectedEtfs, etf];
                setSelectedEtfs(next);
                if (!next.includes(etf)) {
                  setWeights((w) => ({ ...w, [etf]: 0 }));
                } else {
                  const u = 1 / next.length;
                  setWeights((w) => {
                    const nw = { ...w };
                    next.forEach((x) => (nw[x] = u));
                    return nw;
                  });
                }
              }}
            />{" "}
            {etf}
          </label>
        ))}
      </div>

      <h3>{L(lang, "Step 2: Imposta pesi", "Step 2: Set weights")}</h3>
      <table className="weights-table">
        <thead>
          <tr><th>ETF</th><th>{L(lang, "Peso", "Weight")}</th></tr>
        </thead>
        <tbody>
          {selectedEtfs.map((etf) => (
            <tr key={etf}>
              <td>{etf}</td>
              <td>
                <input
                  type="number"
                  step={0.01}
                  min={0}
                  max={1}
                  value={weights[etf] || 0}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value) || 0;
                    setWeights((w) => ({ ...w, [etf]: v }));
                  }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <small style={{ color: valid ? "green" : "red" }}>
        {valid
          ? L(lang, "Somma pesi OK", "Weight sum OK")
          : L(lang, "La somma dei pesi deve essere 1", "Weights must sum to 1")}
      </small>

      <h3>{L(lang, "Step 3: Parametri simulazione", "Step 3: Simulation parameters")}</h3>
      {L(lang, "Orizzonte (anni): ", "Horizon (years): ")}
      <input type="number" value={years} min={1} max={30} onChange={(e) => setYears(+e.target.value)} style={{ width: 50 }} />{" "}
      {L(lang, "Iniziale: ", "Initial: ")}
      <input type="number" value={initial} onChange={(e) => setInitial(+e.target.value)} style={{ width: 90 }} />{" "}
      {L(lang, "Mensile: ", "Monthly: ")}
      <input type="number" value={monthly} onChange={(e) => setMonthly(+e.target.value)} style={{ width: 90 }} />{" "}
      {L(lang, "Simulazioni: ", "Simulations: ")}
      <input
        type="number"
        value={simulations}
        min={100}
        max={2000}
        onChange={(e) => setSimulations(+e.target.value)}
        style={{ width: 60 }}
      />{" "}

      <br /><br />
      <button className="sim-btn" disabled={!valid || !!validateInputs()} onClick={handleSimula}>
        {L(lang, "Avvia", "Run")}
      </button>
      {error && <div style={{ color: "red", marginTop: "0.5rem" }}>{error}</div>}

      {(wealthPath.length > 0 || simResults) && (
        <div className="charts-container">
          <div className="chart-wrapper">
            <BacktestChart
              ref={backtestRef}
              history={wealthPath}
              labels={alignedLabels}
              lang={lang}
            />

            <MetricsTable
              metrics={backtestPortfolioMetrics}
              title={L(
                lang,
                "Metriche Backtest – Portafoglio (senza flussi)",
                "Backtest Metrics – Portfolio (no flows)"
              )}
            />

            <MetricsTable
              metrics={backtestInvestorMetrics}
              title={L(
                lang,
                "Metriche Backtest – Investitore (con versamenti)",
                "Backtest Metrics – Investor (with contributions)"
              )}
            />
          </div>

          <div className="chart-wrapper">
            <HibbsChart ref={simRef} simResults={simResults} lang={lang} />

            <MetricsTable
              metrics={simPortfolioMetrics}
              title={L(
                lang,
                "Metriche Simulazione – Portafoglio (no flussi)",
                "Simulation Metrics – Portfolio (no flows)"
              )}
            />

            <MetricsTable
              metrics={simInvestorMetrics}
              title={L(
                lang,
                "Metriche Simulazione – Investitore (con versamenti)",
                "Simulation Metrics – Investor (with contributions)"
              )}
            />
          </div>
        </div>
      )}


      {/* Disclaimer educativo + ipotesi sui dati — visibile in tutte le modalità
          (prima solo EDU/PRE: in POST mancava). Revisione 29/09/2026. */}
      <div style={{ textAlign: "center", opacity: 0.8, marginTop: "8px", fontSize: 12 }}>
        {L(lang,
          "Contenuto educativo dimostrativo: non costituisce consulenza in materia di investimenti né raccomandazione personalizzata. Dati: serie dimostrative di prezzi giornalieri di 10 panieri azionari settoriali globali (gen 2007 – ago 2025), con nomi generici che non identificano prodotti acquistabili. Importi in dollari USA senza conversione valutaria; valori nominali, al lordo di costi, imposte e inflazione. I rendimenti passati o simulati non sono indicativi di quelli futuri.",
          "Demonstrative educational content: not investment advice or a personalised recommendation. Data: demonstration series of daily prices of 10 global equity sector baskets (Jan 2007 – Aug 2025), with generic names that do not identify purchasable products. Amounts in US dollars with no currency conversion; nominal values, gross of costs, taxes and inflation. Past or simulated returns are not indicative of future ones.")}
      </div>

      {/* PDF Download — temporaneamente disabilitato, riabilitare quando serve
      <div style={{ textAlign: "center", marginTop: "2rem" }}>
        <h4>{L(lang, "Scarica Report PDF", "Download PDF Report")}</h4>
        <button className="sim-btn" onClick={handleDownloadPdf} disabled={!simResults}>
          {L(lang, "Scarica PDF", "Download PDF")}
        </button>
      </div>
      */}
    </div>
  );
}
